import React, { useState, useEffect, useMemo, memo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import { useAuth } from '../../../../context/AuthContext';
import CompactRangePicker from '../CompactRangePicker';
import { useLeaderExport } from './useLeaderExport';
import LeaderPdfDocument from './LeaderPdfDocument';
import { formatDate, formatDateCompact } from '../../../../utils/dateUtils';
import { getResourceColor } from '../../../../utils/resourceUtils';
import PdfSilentRunner from './components/PdfSilentRunner';
import { useSafeClose } from '../../../../hooks/useSafeClose';

// Neue modulare Komponenten
import SectionHeader from './components/SectionHeader';
import ToggleItem from './components/ToggleItem';
import MultiSelectDropdown from './components/MultiSelectDropdown';
import HierarchicalVehicleFilter from './components/HierarchicalVehicleFilter';
import AssignmentSelectionCard from './components/AssignmentSelectionCard';
import VirtualAssignmentList from './components/VirtualAssignmentList';

import './LeaderExportModal.css';

/**
 * LeaderExportModal - Das zentrale Interface für den PDF-Export.
 * Orchestriert Filter, Auswahl und PDF-Vorschau.
 * 
 * @param {Object} props - Komponenteneigenschaften
 * @param {boolean} props.isOpen - Sichtbarkeit des Modals
 * @param {Function} props.onClose - schließt das Modal
 */
const LeaderExportModal = memo(({
    isOpen,
    onClose,
    assignments = [],
    workers = [],
    vehicles = [],
    kolonnen = [],
    isReadOnly = false,
    workerId = null,
    initialSelectedIds = [],
    onAssignmentClick,
    onAssignmentHover,
    onAssignmentHoverEnd,
    isCovered = false,
    companyLoc = null,
    initialFilters = null
}) => {
    const { user } = useAuth();
    const exportLogic = useLeaderExport({
        assignments,
        workers,
        vehicles,
        kolonnen,
        isReadOnly,
        workerId,
        initialSelectedIds,
        initialFilters
    });

    const {
        searchTerm, setSearchTerm,
        dateRange, setDateRange,
        statusFilter, setStatusFilter,
        workerFilter, setWorkerFilter,
        vehicleFilter, setVehicleFilter,
        kolonnenFilter, setKolonnenFilter,
        selectedAssignmentIds,
        expandedSections, toggleSection,
        openDropdown, setOpenDropdown,
        options, toggleOption,
        sortOrder, setSortOrder,
        dateMode, setDateMode,
        filteredCandidates,
        finalAssignments,
        effectiveAssignments,
        toggleAssignment,
        selectAllCandidates,
        deselectAllCandidates,
        injectMockData,
        resetAll,
        workerSearchTerm, setWorkerSearchTerm,
        vehicleSearchTerm, setVehicleSearchTerm,
        kolonnenSearchTerm, setKolonnenSearchTerm,
        statusSearchTerm, setStatusSearchTerm
    } = exportLogic;

    // Memoized filtered lists for the PDF
    const memoWorkers = useMemo(() => workers.filter(w => workerFilter.includes(w.id)), [workers, workerFilter]);
    const memoVehicles = useMemo(() => vehicles.filter(v => vehicleFilter.includes(v.id)), [vehicles, vehicleFilter]);
    const memoKolonnen = useMemo(() => kolonnen.filter(k => kolonnenFilter.includes(k.id)), [kolonnen, kolonnenFilter]);

    const [debouncedAssignments, setDebouncedAssignments] = useState(finalAssignments);
    const [debouncedOptions, setDebouncedOptions] = useState(options);
    const [debouncedDateRange, setDebouncedDateRange] = useState(dateRange);
    const [debouncedWorkers, setDebouncedWorkers] = useState(memoWorkers);
    const [debouncedVehicles, setDebouncedVehicles] = useState(memoVehicles);
    const [debouncedKolonnen, setDebouncedKolonnen] = useState(memoKolonnen);
    const [debouncedDateMode, setDebouncedDateMode] = useState(dateMode);

    // --- Performance & Preview Control ---
    const [lastManualUpdate, setLastManualUpdate] = useState(Date.now());
    const isPreviewPaused = finalAssignments.length > 30;
    const isExportBlocked = finalAssignments.length > 300;

    // --- Stabilisierung der Identitäten (Phase 7.14) ---
    const stableCompanyName = useMemo(() => user?.company_name || 'Optimox Manager', [user?.company_name]);
    const stableUserName = useMemo(() => user?.name || 'Unbekannt', [user?.name]);

    // --- Stabilisierter Zeitstempel (Phase 7.17) ---
    const [stableExportDate, setStableExportDate] = useState(() => {
        return new Date().toLocaleString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    });

    const debouncedStateRef = useRef({
        assignments: '',
        options: '',
        workers: '',
        vehicles: '',
        kolonnen: '',
        dateRange: '',
        dateMode: ''
    });

    const rawContentKey = useMemo(() => {
        return JSON.stringify({
            ids: Array.from(selectedAssignmentIds).sort(),
            options,
            dateRange,
            workers: [...workerFilter].sort(),
            vehicles: [...vehicleFilter].sort(),
            kolonnen: [...kolonnenFilter].sort(),
            dateMode
        });
    }, [selectedAssignmentIds, options, dateRange, workerFilter, vehicleFilter, kolonnenFilter, dateMode]);

    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    const pdfPackage = useMemo(() => {
        // Atomic Payload: Alle Daten in einen stabilen JSON-String serialisieren
        const payload = JSON.stringify({
            assignments: debouncedAssignments,
            workers: debouncedWorkers,
            vehicles: debouncedVehicles,
            kolonnen: debouncedKolonnen,
            options: debouncedOptions,
            dateRange: debouncedDateRange,
            dateMode: debouncedDateMode,
            companyName: stableCompanyName,
            userName: stableUserName,
            exportDate: stableExportDate,
            companyLoc
        });

        // Der Keyist jetzt identisch mit dem Payload-Hash (Inhalt bestimmt Identität)
        return { payload, key: payload };
    }, [
        debouncedAssignments, debouncedOptions, debouncedDateRange,
        debouncedWorkers, debouncedVehicles, debouncedKolonnen,
        stableUserName, debouncedDateMode, stableCompanyName, stableExportDate,
        companyLoc
    ]);

    useEffect(() => {
        if (isPreviewPaused) return;

        setIsPreviewLoading(true);
        const timer = setTimeout(() => {
            // Deep-Equality Check (Phase 7.17)
            const newAssignmentsStr = JSON.stringify(finalAssignments);
            const newOptionsStr = JSON.stringify(options);
            const newWorkersStr = JSON.stringify(memoWorkers);
            const newVehiclesStr = JSON.stringify(memoVehicles);
            const newKolonnenStr = JSON.stringify(memoKolonnen);
            const newDateRangeStr = JSON.stringify(dateRange);

            let hasChanged = false;

            if (debouncedStateRef.current.assignments !== newAssignmentsStr) {
                setDebouncedAssignments(finalAssignments);
                debouncedStateRef.current.assignments = newAssignmentsStr;
                hasChanged = true;
            }
            if (debouncedStateRef.current.options !== newOptionsStr) {
                setDebouncedOptions(options);
                debouncedStateRef.current.options = newOptionsStr;
                hasChanged = true;
            }
            if (debouncedStateRef.current.workers !== newWorkersStr) {
                setDebouncedWorkers(memoWorkers);
                debouncedStateRef.current.workers = newWorkersStr;
                hasChanged = true;
            }
            if (debouncedStateRef.current.vehicles !== newVehiclesStr) {
                setDebouncedVehicles(memoVehicles);
                debouncedStateRef.current.vehicles = newVehiclesStr;
                hasChanged = true;
            }
            if (debouncedStateRef.current.kolonnen !== newKolonnenStr) {
                setDebouncedKolonnen(memoKolonnen);
                debouncedStateRef.current.kolonnen = newKolonnenStr;
                hasChanged = true;
            }
            if (debouncedStateRef.current.dateRange !== newDateRangeStr) {
                setDebouncedDateRange(dateRange);
                debouncedStateRef.current.dateRange = newDateRangeStr;
                hasChanged = true;
            }
            if (debouncedStateRef.current.dateMode !== dateMode) {
                setDebouncedDateMode(dateMode);
                debouncedStateRef.current.dateMode = dateMode;
                hasChanged = true;
            }

            if (hasChanged) {
                setIsGenerating(true);
                // Zeitstempel nur bei inhaltlicher Änderung auffrischen
                setStableExportDate(new Date().toLocaleString('de-DE', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                }));
            }

            setIsPreviewLoading(false);
            // Nach dem Update setzen wir isGenerating im Modal wieder auf false,
            // da die PDF-Komponente ab jetzt übernimmt.
            setTimeout(() => setIsGenerating(false), 500);
        }, 800);
        return () => clearTimeout(timer);
    }, [rawContentKey, lastManualUpdate, finalAssignments.length]);

    // Initialisierungs-Sicherung: Wenn die echten Daten eintreffen, debounced State füllen
    useEffect(() => {
        if (debouncedAssignments.length === 0 && finalAssignments.length > 0) {
            const newAssignmentsStr = JSON.stringify(finalAssignments);
            setDebouncedAssignments(finalAssignments);
            debouncedStateRef.current.assignments = newAssignmentsStr;

            setDebouncedOptions(options);
            setDebouncedWorkers(memoWorkers);
            setDebouncedVehicles(memoVehicles);
            setDebouncedKolonnen(memoKolonnen);
            setDebouncedDateRange(dateRange);
            setDebouncedDateMode(dateMode);
        }
    }, [finalAssignments.length, isOpen]);



    // Manueller Trigger für die Vorschau
    const handleManualPreviewUpdate = () => {
        setIsPreviewLoading(true);
        setDebouncedAssignments(finalAssignments);
        setDebouncedOptions(options);
        setDebouncedDateRange(dateRange);
        setDebouncedDateMode(dateMode);
        setDebouncedWorkers(memoWorkers);
        setDebouncedVehicles(memoVehicles);
        setDebouncedKolonnen(memoKolonnen);
        setIsPreviewLoading(false);
    };

    // (companyName und userName wurden nach oben verschoben)

    const groupedVehicles = useMemo(() => {
        const groups = {};
        vehicles.forEach(v => {
            const cat = v.category_name || 'Sonstige';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(v);
        });
        return groups;
    }, [vehicles]);

    const periodLabel = useMemo(() => {
        if (dateMode === 'undated') return "Undatiert";
        let label = "Gesamt";
        if (dateRange.start || dateRange.end) {
            const start = formatDateCompact(dateRange.start);
            const end = formatDateCompact(dateRange.end);
            label = start === end ? start : `${start} - ${end}`;
        }
        return dateMode === 'combined' ? `${label} + Undatiert` : label;
    }, [dateRange, dateMode]);

    // --- PDF Bunker Bridge (Phase 7.4) ---
    // (pdfDataRef wurde nach oben verschoben)
    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    if (!isOpen) return null;

    return createPortal(
        <div
            className="export-modal-overlay"
            ref={overlayRef}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            style={{
                visibility: isCovered ? 'hidden' : 'visible',
                opacity: isCovered ? 0 : 1,
                pointerEvents: isCovered ? 'none' : 'auto',
                transition: 'opacity 0.2s ease, visibility 0.2s ease'
            }}
        >
            <div className="export-modal-container" onClick={e => e.stopPropagation()}>
                <div className="export-modal-header">
                    <h2>📄 PDF Export</h2>
                    <button className="export-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="export-modal-content">
                    <div className="export-sidebar">
                        <div className="export-sidebar-scroll">
                            <div className="export-sidebar-section">
                                <SectionHeader
                                    id="search"
                                    icon="📅"
                                    title="Zeitraum"
                                    isExpanded={expandedSections.has('search')}
                                    onToggle={toggleSection}
                                >
                                    <span className="export-section-subtitle">{periodLabel}</span>
                                </SectionHeader>
                                {expandedSections.has('search') && (
                                    <div className="export-section-content">
                                        <div className="export-setting-group">
                                            <div className="export-segmented-control">
                                                <button className={`segment-btn ${dateMode === 'calendar' ? 'active' : ''}`} onClick={() => setDateMode('calendar')}>Kalender</button>
                                                <button className={`segment-btn ${dateMode === 'undated' ? 'active' : ''}`} onClick={() => setDateMode('undated')}>Undatiert</button>
                                                <button className={`segment-btn ${dateMode === 'combined' ? 'active' : ''}`} onClick={() => setDateMode('combined')}>Alles</button>
                                            </div>
                                        </div>
                                        {dateMode !== 'undated' && (
                                            <CompactRangePicker
                                                startDate={dateRange.start}
                                                endDate={dateRange.end}
                                                onSelect={(start, end) => setDateRange({ start, end: end || start })}
                                                onClear={() => setDateRange({ start: null, end: null })}
                                                placeholder="Zeitraum wählen"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="export-sidebar-section">
                                <SectionHeader
                                    id="filters"
                                    icon="⚙️"
                                    title="Filter"
                                    isExpanded={expandedSections.has('filters')}
                                    onToggle={toggleSection}
                                />
                                {expandedSections.has('filters') && (
                                    <div className="export-section-content">
                                        <MultiSelectDropdown
                                            id="status"
                                            label="Status"
                                            options={[
                                                { id: 'aktiv', name: 'Aktiv', color: '#4f46e5' },
                                                { id: 'pausiert', name: 'Pausiert', color: '#f59e0b' },
                                                { id: 'abgeschlossen', name: 'Abgeschlossen', color: '#059669' }
                                            ]}
                                            selected={statusFilter}
                                            isOpen={openDropdown === 'status'}
                                            onToggle={setOpenDropdown}
                                            onChange={setStatusFilter}
                                            searchTerm={statusSearchTerm}
                                            onSearchChange={setStatusSearchTerm}
                                        />
                                        <MultiSelectDropdown
                                            id="kolonnen"
                                            label="Kolonnen"
                                            options={[
                                                { id: 'none', name: 'Ohne Kolonne' },
                                                ...kolonnen.map(k => ({ id: k.id, name: k.internal_name || k.name }))
                                            ]}
                                            selected={kolonnenFilter}
                                            isOpen={openDropdown === 'kolonnen'}
                                            onToggle={setOpenDropdown}
                                            onChange={setKolonnenFilter}
                                            searchTerm={kolonnenSearchTerm}
                                            onSearchChange={setKolonnenSearchTerm}
                                        />
                                        <MultiSelectDropdown
                                            id="workers"
                                            label="Mitarbeiter"
                                            options={workers.map(w => ({ id: w.id, name: w.name, color: getResourceColor(w) }))}
                                            selected={workerFilter}
                                            isOpen={openDropdown === 'workers'}
                                            onToggle={setOpenDropdown}
                                            onChange={setWorkerFilter}
                                            searchTerm={workerSearchTerm}
                                            onSearchChange={setWorkerSearchTerm}
                                        />
                                        <HierarchicalVehicleFilter
                                            selected={vehicleFilter}
                                            onToggle={setVehicleFilter}
                                            onAll={() => setVehicleFilter(vehicles.map(v => v.id))}
                                            onNone={() => setVehicleFilter([])}
                                            openDropdown={openDropdown}
                                            setOpenDropdown={setOpenDropdown}
                                            groupedVehicles={groupedVehicles}
                                            totalCount={vehicles.length}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="export-sidebar-section">
                                <SectionHeader
                                    id="options"
                                    icon="🎨"
                                    title="Anzeige"
                                    isExpanded={expandedSections.has('options')}
                                    onToggle={toggleSection}
                                />
                                {expandedSections.has('options') && (
                                    <div className="export-section-content">
                                        <div className="export-setting-group">
                                            <div className="export-label-small">Berichtstyp</div>
                                            <div className="export-segmented-control-premium">
                                                <button
                                                    className={`segment-btn-premium ${options.exportStyle === 'compact' ? 'active' : ''}`}
                                                    onClick={() => toggleOption('exportStyle', 'compact')}
                                                >
                                                    Kompakt
                                                </button>
                                                <button
                                                    className={`segment-btn-premium ${options.exportStyle === 'detailed' ? 'active' : ''}`}
                                                    onClick={() => toggleOption('exportStyle', 'detailed')}
                                                >
                                                    Detailliert
                                                </button>
                                            </div>
                                        </div>

                                        <div className="export-options-group">
                                            <div className="export-group-title">Ressourcen-Struktur</div>
                                            <ToggleItem label="Kolonnenliste" icon="🏗️" value={options.showKolonnenList} onChange={() => toggleOption('showKolonnenList')} />
                                            <ToggleItem label="Mitarbeiterliste" icon="👥" value={options.showWorkerList} onChange={() => toggleOption('showWorkerList')} />
                                            <ToggleItem label="Fahrzeugliste" icon="🚛" value={options.showVehicleList} onChange={() => toggleOption('showVehicleList')} />
                                        </div>

                                        <div className="export-options-group">
                                            <div className="export-group-title">Dokument-Inhalt</div>
                                            <ToggleItem label="Notizen" icon="📒" value={options.showNotes} onChange={() => toggleOption('showNotes')} />
                                            <ToggleItem label="Unterschriften" icon="✍️" value={options.showSignatures} onChange={() => toggleOption('showSignatures')} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="export-sidebar-actions">
                            <div className="export-stats">
                                <strong>{finalAssignments.length}</strong> Einsätze gewählt
                                {isExportBlocked && (
                                    <div className="export-limit-warning">
                                        ⚠️ Max. 300 Items erlaubt
                                    </div>
                                )}
                            </div>
                            <PDFDownloadLink
                                document={<LeaderPdfDocument assignments={debouncedAssignments} workers={debouncedWorkers} vehicles={debouncedVehicles} kolonnen={debouncedKolonnen} options={debouncedOptions} companyName={user?.company_name || 'Optimox Manager'} userName={user?.name || 'Unbekannt'} dateRange={debouncedDateRange} dateMode={debouncedDateMode} companyLoc={companyLoc} />}
                                fileName={`Arbeitsbericht_${new Date().toISOString().split('T')[0]}.pdf`}
                            >
                                {({ loading }) => (
                                    <button
                                        className="btn-sidebar-download"
                                        disabled={loading || finalAssignments.length === 0 || isExportBlocked}
                                    >
                                        {loading ? 'Lädt...' : '📄 PDF Herunterladen'}
                                    </button>
                                )}
                            </PDFDownloadLink>
                            <button className="btn-sidebar-reset" onClick={resetAll}>🔄 Alle zurücksetzen</button>
                            <button className="btn-sidebar-cancel" onClick={onClose}>Abbrechen</button>
                            {process.env.NODE_ENV === 'development' && (
                                <button
                                    className="btn-debug-stress"
                                    onClick={injectMockData}
                                    style={{ marginTop: 8, fontSize: '9px', opacity: 0.5, border: '1px dashed #cbd5e1', background: 'transparent' }}
                                >
                                    {effectiveAssignments.length > assignments.length ? '🧹 Mock Daten entfernen' : '🛠 Stress Test (1000 Items)'}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={`export-details-toggle ${isDetailsOpen ? 'open' : ''}`} onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
                        <span className="toggle-text">Einsätze wählen</span>
                        <span className="toggle-chevron">{isDetailsOpen ? '◀' : '▶'}</span>
                    </div>

                    <div className={`export-details-panel ${isDetailsOpen ? 'open' : ''}`}>
                        <div className="details-panel-header">
                            <h3>Auswahl ({filteredCandidates.length})</h3>
                            <button className="details-close" onClick={() => setIsDetailsOpen(false)}>◀</button>
                        </div>
                        <div className="details-search-wrapper">
                            <span className="details-search-icon">🔍</span>
                            <input type="text" placeholder="Suchen..." className="details-search-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                        </div>
                        <div className="details-bulk-actions">
                            <button onClick={selectAllCandidates}>Alle</button>
                            <button onClick={deselectAllCandidates}>Keine</button>
                        </div>
                        <VirtualAssignmentList
                            items={filteredCandidates}
                            selectedIds={selectedAssignmentIds}
                            onToggle={toggleAssignment}
                            onInfoClick={onAssignmentClick}
                            onHover={onAssignmentHover}
                            onHoverEnd={onAssignmentHoverEnd}
                        />
                    </div>

                    <div className="export-preview-area">
                        <div className="export-preview-container">
                            <PdfSilentRunner payload={pdfPackage.payload} />
                        </div>
                    </div>
                </div>

            </div>
        </div>,
        document.body
    );
}, (prevProps, nextProps) => {
    // Das Modal soll nur neu rendern, wenn sich der Öffnungszustand oder die Basisdaten ändern.
    // Hover-Events oder parent-induzierte Rerenders ohne Datenänderung werden ignoriert.
    return (
        prevProps.isOpen === nextProps.isOpen &&
        prevProps.assignments === nextProps.assignments &&
        prevProps.workers === nextProps.workers &&
        prevProps.vehicles === nextProps.vehicles &&
        prevProps.kolonnen === nextProps.kolonnen &&
        prevProps.isReadOnly === nextProps.isReadOnly &&
        prevProps.isCovered === nextProps.isCovered
    );
});

LeaderExportModal.displayName = 'LeaderExportModal';

export default LeaderExportModal;
